import "./contents.css";
import * as React from "react";
import { Container, Button, Label } from "reactstrap";
import i18n from "../i18n";
import OpenScenario from "designer/open-scenario";
import { getAsync, postAsync } from "utils/net";
import { ScenarioModel } from "models/scenario";
import { deserialize, serialize } from "designer/serializer";
import { Object } from "core-js";
import ReactTable, { Column } from "react-table";
import { NodeModel, DiagramModel } from "storm-react-diagrams";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { checkUser, checkRole } from "security/security";
import { faDownload, faUpload, faPencilAlt, faSave, faRedo } from "@fortawesome/free-solid-svg-icons";
import ContentEdit from "./content-edit";
import { Content } from "models/content";
import { ToastsStore } from "react-toasts"
import { ModalNotification } from "../utils/modalNotification";

export interface ContentsTableProps { }
export interface ContentsTableState {
    loading: boolean;
    scenarios: any[];
    currentScenario: ScenarioModel | null;
    currentModel: DiagramModel | null;
    editableNodes: NodeModel[];
    locked: boolean,
    hasChanges: boolean;
    showEdit: boolean;
    selectedItem: any;
    showModal: boolean;
    modalMessage: string;
    modalResolve?: () => void;
}

export class ContentsTable extends React.Component<
    ContentsTableProps,
    ContentsTableState
    > {

    private readonly editableWidgets = [
        "Attachment",
        "Message",
        "MessageEnd",
        "SendMail",
        "SendSMS",
        "Text"
    ];

    private filterMethod = (filter: any, row: any) =>
        new RegExp(filter.value, "i").test(String(row[filter.id]));

    private readonlyColumns: Column[] = [
        {
            Header: i18n.Contents.Table.Type,
            accessor: "type"
        },
        {
            Header: i18n.Contents.Table.Description,
            accessor: "description"
        },
        {
            Header: i18n.Contents.Table.Message,
            accessor: "message"
        },
        {
            Header: i18n.Contents.Table.Speak,
            accessor: "speak"
        }
    ];

    private editableColumns: Column[] = [
        {
            Header: i18n.Contents.Table.Type,
            accessor: "type",
            maxWidth: 100,
        },
        {
            Header: i18n.Contents.Table.Description,
            accessor: "description"
        },
        {
            Header: i18n.Contents.Table.Message,
            accessor: "message"
        },
        {
            Header: i18n.Contents.Table.Speak,
            accessor: "speak"
        },
        {
            Header: "",
            show: true,
            filterable: false,
            maxWidth: 50,
            Cell: row =>
                (
                    <Button
                        size="sm"
                        color="primary"
                        onClick={() => this.onEdit(row.original)
                        }
                    >
                        <FontAwesomeIcon icon={faPencilAlt} />
                    </Button >
                )
        }
    ];

    constructor(props: ContentsTableProps) {
        super(props);

        this.state = {
            loading: false,
            scenarios: [],
            currentScenario: null,
            currentModel: null,
            editableNodes: [],
            locked: false,
            hasChanges: false,
            showEdit: false,
            selectedItem: null,
            showModal: false,
            modalMessage: ""
        };

        this.loadScenarioList();

        this.showModal = this.showModal.bind(this);
        this.hideModal = this.hideModal.bind(this);
    }

    private showModal(message: string): Promise<void> {
        return new Promise((resolve) => {
            this.setState({ showModal: true, modalMessage: message, modalResolve: resolve });
        });
    }

    private hideModal() {
        this.setState({ showModal: false, modalMessage: "" }, () => {
            if (this.state.modalResolve) {
                this.state.modalResolve();
                this.setState({ modalResolve: undefined });
            }
        });
    }

    render() {
        return (
            <Container fluid={true} className="scenarios-page page">
                <div className="containerHeader">
                    <h1 className="page-header">{i18n.Contents.Title} {
                        this.state.currentScenario ? this.state.currentScenario.partitionKey : ""}
                    </h1>
                    {this.showToolbox()}
                </div>
                <p className="subheader">
                    <Label>
                        <b>{i18n.Contents.Form.ChangedBy}: </b>
                        {this.state.currentScenario ? this.state.currentScenario.changedBy : ""}
                    </Label>
                    <Label>
                        <b>{i18n.Contents.Form.CheckedOutBy}: </b>
                        {this.state.currentScenario ? this.state.currentScenario.checkedOutBy : ""}
                    </Label>
                </p>
                <ReactTable
                    noDataText={this.state.loading ? i18n.Global.Loading : i18n.Contents.Table.NoData}
                    data={this.list}
                    filterable={true}
                    columns={this.state.locked ? this.editableColumns : this.readonlyColumns}
                    defaultFilterMethod={this.filterMethod}
                    defaultPageSize={10}
                    className="-striped -highlight"
                />
                <ContentEdit
                    isOpen={this.state.showEdit}
                    content={this.state.selectedItem}
                    onCancel={() => this.onCancelEdit()}
                    onSave={content => this.onSaveEdit(content)}
                />
                <ModalNotification
                    modalMessage={this.state.modalMessage}
                    showModal={this.state.showModal}
                    onClose={this.hideModal}
                />
            </Container>
        );
    }

    private showToolbox() {
        return (
            <span>
                {!this.state.locked &&
                    (<OpenScenario
                        scenarios={this.state.scenarios}
                        onSelected={model => this.loadModel(model)}
                    />)
                }
                {checkRole("Contents.Change") && this.state.currentScenario && !this.state.locked &&
                    (<Button
                        color="warning"
                        title={i18n.Contents.Form.CheckOutButton}
                        onClick={() => this.onCheckoutScenario()}
                    >
                        <FontAwesomeIcon icon={faDownload} />
                    </Button>)}
                {this.state.locked && this.state.hasChanges &&
                    (<Button
                        color="primary"
                        title={i18n.Contents.Form.SaveScenarioButton}
                        onClick={() => this.onSaveScenario()}
                    >
                        <FontAwesomeIcon icon={faSave} />
                    </Button>)}
                {this.state.locked && !this.state.hasChanges &&
                    (<Button
                        color="warning"
                        title={i18n.Contents.Form.CheckInButton}
                        onClick={() => this.onCheckinScenario()}
                    >
                        <FontAwesomeIcon icon={faUpload} />
                    </Button>)}
                {this.state.locked && this.state.hasChanges &&
                    (<Button
                        color="primary"
                        title={i18n.Contents.Form.ResetChangesButton}
                        onClick={() => this.loadModel(this.state.currentScenario)}
                    >
                        <FontAwesomeIcon icon={faRedo} />
                    </Button>)}
            </span>);
    }

    private async loadScenarioList() {
        this.setState({ loading: true });

        const reply = await getAsync("/api/designer/GetScenariosList");
        const payload = await reply.json();
        this.setState({
            scenarios: payload,
            loading: false
        });
    }

    private async loadModel(scenario: ScenarioModel | null) {
        this.setState({ loading: true });

        if (scenario) {
            const reply = await getAsync(
                `/api/designer/getscenario/${scenario.id}?version=${
                scenario.rowKey
                }`
            );

            const payload = await reply.json();
            scenario.changedBy = payload.ChangedBy;
            scenario.checkedOutBy = payload.CheckedOutBy;
            scenario.definition = payload.Definition;

            this.deserializeJson(scenario);
        } else {
            this.setState({ loading: false });
            ToastsStore.error("Failed to load scenario");
        }
    }

    private deserializeJson(scenario: ScenarioModel) {
        const model = deserialize(scenario.definition);
        const editableNodes = this.getEditableNodes(model);

        this.setState({
            loading: false,
            currentScenario: scenario,
            currentModel: model,
            editableNodes: editableNodes,
            locked: checkUser(scenario.checkedOutBy),
            hasChanges: false
        });
    }

    private getEditableNodes(model: DiagramModel) {
        const nodesList = Object.values(model.getNodes());
        return nodesList.filter(n => n.extras && this.editableWidgets.some(
            w => w === n.extras.type && n.extras.publisher === "Yes"));
    }

    private async onCheckoutScenario() {
        // Make sure that the user is aware of the fact that this scenario is being edited by another user or not:
        await this.loadModel(this.state.currentScenario);

        let confirmMessage = "Do you wish to checkout this scenario?";
        if (this.state.currentScenario !== null && this.state.currentScenario.checkedOutBy
            && !checkUser(this.state.currentScenario.checkedOutBy)) {
            confirmMessage =
                "WARNING: THERE IS ANOTHER USER (" + this.state.currentScenario.checkedOutBy
                + ") ALREADY WORKING ON THIS SCENARIO!\n" + confirmMessage;
        }
        if (!confirm(confirmMessage)) {
            return;
        }

        const reply = await postAsync(
            "/api/designer/checkout",
            this.state.currentScenario
        );
        const updatedModel = await reply.json();

        await this.loadScenarioList();

        this.setState({
            currentScenario: updatedModel,
            locked: true
        });

        ToastsStore.success("Checkout completed!");
    }

    private async onSaveScenario() {
        if (!confirm("Are you sure to save?")) {
            return;
        }

        if (this.state.currentScenario && this.state.currentModel) {
            const model = this.state.currentModel;
            const nodesDict = model.getNodes();
            this.state.editableNodes.forEach(n => {
                nodesDict[n.id].extras.q = n.extras.q;
                nodesDict[n.id].extras.speak = n.extras.speak;
            });

            const updatedDefinition = serialize(model);
            const json = JSON.stringify(updatedDefinition);

            if (json.length > 2 * 1024 * 1024) {
                await this.showModal(i18n.Designer.Toolbox.ExceededTechnicalLimit);
                return;
            } else if (json.length > 1.5 * 1024 * 1024) {
                await this.showModal(
                    `Definition size is approaching the technical limit (currently ${json.length}). 
                    Please consider splitting this scenario.`
                );
            }

            let response = await postAsync("/api/designer/SaveDefinition", {
                ...this.state.currentScenario,
                definition: json
            });

            let result = await response.json();

            this.setState({ hasChanges: false });

            if (result.success) {
                ToastsStore.success("Everything was saved!");
            } else {
                ToastsStore.error(result.message);
            }

        } else {
            ToastsStore.error("Error: cannot read current scenario");
        }
    }

    private async onCheckinScenario() {
        if (!confirm("Are you sure to checkin this scenario and ALL related start nodes?")) {
            return;
        }

        const reply = await postAsync(
            "/api/designer/checkin",
            this.state.currentScenario
        );
        const updatedModel = await reply.json();

        await this.loadScenarioList();

        this.setState({
            currentScenario: updatedModel,
            locked: false,
        });
        ToastsStore.success("Checkin completed!");
    }

    private onEdit(item: any) {
        this.setState({ showEdit: true, selectedItem: item });
    }

    private onCancelEdit() {
        this.setState({ showEdit: false, selectedItem: null });
    }

    private onSaveEdit(newContent: Content) {
        let hasChanged = false;
        const newEditableNodes = this.state.editableNodes.map(n => {
            if (n.id === newContent.id && (n.extras.q !== newContent.message || n.extras.speak !== newContent.speak)) {
                n.extras.q = newContent.message;
                n.extras.speak = newContent.speak;
                hasChanged = true;
            }
            return n;
        })

        this.setState({
            hasChanges: hasChanged,
            editableNodes: newEditableNodes,
            showEdit: false,
        });
    }

    private get list(): Content[] {
        return this.state.editableNodes.map(n => ({
            id: n.id,
            type: n.extras.type,
            description: n.extras.description,
            message: n.extras.q,
            speak: n.extras.speak
        }));
    }
}


// WEBPACK FOOTER //
// ./src/contents/contents.tsx